'use client';

/**
 * Incident Tracker Page
 * Manage and track incidents, outages, and their resolution
 */

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { WorkspaceProvider, useWorkspace } from '@/lib/archimate/WorkspaceContext';
import { CommandCenterProvider, useCommandCenter } from '@/lib/command-center/CommandCenterContext';
import CommandCenterHeader from '@/components/command-center/CommandCenterHeader';

interface Incident {
  id: string;
  workspace_id: string;
  title: string;
  description?: string;
  severity: 'sev1' | 'sev2' | 'sev3' | 'sev4';
  status: 'detected' | 'investigating' | 'identified' | 'mitigating' | 'resolved' | 'postmortem';
  detected_at: string;
  resolved_at?: string;
  incident_commander?: string;
  affected_services?: string[];
  root_cause?: string;
  resolution?: string;
  timeline_entries?: Array<{
    timestamp: string;
    message: string;
    author: string;
  }>;
  ticket_url?: string;
  postmortem_url?: string;
  tags?: string[];
  created_at: string;
}

interface IncidentStats {
  total: number;
  active: number;
  resolved_this_month: number;
  avg_resolution_hours: number;
}

const SEVERITY_CONFIG = {
  sev1: { label: 'SEV1 - Critical', color: 'bg-red-500 text-white', border: 'border-red-500/50' },
  sev2: { label: 'SEV2 - High', color: 'bg-accent-500 text-white', border: 'border-accent-500/50' },
  sev3: { label: 'SEV3 - Medium', color: 'bg-yellow-500 text-black', border: 'border-yellow-500/50' },
  sev4: { label: 'SEV4 - Low', color: 'bg-blue-500 text-white', border: 'border-blue-500/50' },
};

const STATUS_CONFIG = {
  detected: { label: 'Detected', color: 'bg-red-500' },
  investigating: { label: 'Investigating', color: 'bg-accent-500' },
  identified: { label: 'Identified', color: 'bg-yellow-500' },
  mitigating: { label: 'Mitigating', color: 'bg-blue-500' },
  resolved: { label: 'Resolved', color: 'bg-green-500' },
  postmortem: { label: 'Postmortem', color: 'bg-purple-500' },
};

function formatDuration(startDate: string, endDate?: string): string {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();
  const diffMs = end.getTime() - start.getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

function IncidentsContent() {
  const { currentWorkspace, loading: workspaceLoading } = useWorkspace();
  const { setWorkspaceId } = useCommandCenter();

  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [stats, setStats] = useState<IncidentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Form state
  const [newIncident, setNewIncident] = useState<{
    title: string;
    description: string;
    severity: Incident['severity'];
    incident_commander: string;
    affected_services: string;
    ticket_url: string;
  }>({
    title: '',
    description: '',
    severity: 'sev2',
    incident_commander: '',
    affected_services: '',
    ticket_url: '',
  });

  useEffect(() => {
    if (currentWorkspace?.id) {
      setWorkspaceId(currentWorkspace.id);
    }
  }, [currentWorkspace?.id, setWorkspaceId]);

  const loadIncidents = useCallback(async () => {
    if (!currentWorkspace?.id) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ workspace_id: currentWorkspace.id });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (severityFilter !== 'all') params.append('severity', severityFilter);

      const response = await fetch(`/api/secure/command-center/incidents?${params}`);
      if (!response.ok) throw new Error('Failed to load incidents');

      const data = await response.json();
      setIncidents(data.incidents || []);
      setStats(data.stats || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load incidents');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id, statusFilter, severityFilter]);

  useEffect(() => {
    loadIncidents();
  }, [loadIncidents]);

  const handleCreateIncident = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentWorkspace?.id) return;

    try {
      const response = await fetch('/api/secure/command-center/incidents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: currentWorkspace.id,
          title: newIncident.title,
          description: newIncident.description || undefined,
          severity: newIncident.severity,
          incident_commander: newIncident.incident_commander || undefined,
          affected_services: newIncident.affected_services
            ? newIncident.affected_services.split(',').map(s => s.trim())
            : undefined,
          ticket_url: newIncident.ticket_url || undefined,
        }),
      });

      if (!response.ok) throw new Error('Failed to create incident');

      setShowCreateModal(false);
      setNewIncident({
        title: '',
        description: '',
        severity: 'sev2',
        incident_commander: '',
        affected_services: '',
        ticket_url: '',
      });
      loadIncidents();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create incident');
    }
  };

  if (workspaceLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  const activeIncidents = incidents.filter(i => !['resolved', 'postmortem'].includes(i.status));

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-foreground-tertiary text-sm mb-2">
          <Link href="/command-center" className="hover:text-accent">Command Center</Link>
          <span>/</span>
          <span>Incidents</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Incident Tracker</h1>
            <p className="text-foreground-tertiary mt-1">Track and manage incidents across your systems</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Declare Incident
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4 text-red-200 mb-6">
            {error}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-surface/30 border border-border-muted rounded-lg p-4">
              <p className="text-foreground-tertiary text-sm mb-1">Active Incidents</p>
              <span className={`text-4xl font-bold ${stats.active > 0 ? 'text-red-400' : 'text-green-400'}`}>
                {stats.active}
              </span>
            </div>
            <div className="bg-surface/30 border border-border-muted rounded-lg p-4">
              <p className="text-foreground-tertiary text-sm mb-1">Resolved This Month</p>
              <span className="text-4xl font-bold text-foreground">{stats.resolved_this_month}</span>
            </div>
            <div className="bg-surface/30 border border-border-muted rounded-lg p-4">
              <p className="text-foreground-tertiary text-sm mb-1">Avg Resolution Time</p>
              <span className="text-4xl font-bold text-foreground">
                {stats.avg_resolution_hours > 0 ? `${Math.round(stats.avg_resolution_hours)}h` : '-'}
              </span>
            </div>
            <div className="bg-surface/30 border border-border-muted rounded-lg p-4">
              <p className="text-foreground-tertiary text-sm mb-1">Total Incidents</p>
              <span className="text-4xl font-bold text-foreground">{stats.total}</span>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div>
            <label className="text-xs text-foreground-tertiary mb-1 block">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-surface border border-border rounded-lg px-3 py-1.5 text-foreground text-sm focus:outline-none focus:border-accent"
            >
              <option value="all">All Statuses</option>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-foreground-tertiary mb-1 block">Severity</label>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="bg-surface border border-border rounded-lg px-3 py-1.5 text-foreground text-sm focus:outline-none focus:border-accent"
            >
              <option value="all">All Severities</option>
              {Object.entries(SEVERITY_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Active Incidents Alert */}
        {activeIncidents.length > 0 && (
          <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-red-200">
              <svg className="w-5 h-5 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">{activeIncidents.length} Active Incident{activeIncidents.length > 1 ? 's' : ''}</span>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
          </div>
        )}

        {/* Incidents List */}
        {!loading && (
          <div className="space-y-4">
            {incidents.length === 0 ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cream/5 mb-4">
                  <span className="text-3xl">🎉</span>
                </div>
                <p className="text-foreground-tertiary text-lg">No incidents found</p>
                <p className="text-foreground-muted mt-2">Your systems are running smoothly</p>
              </div>
            ) : (
              incidents.map((incident) => {
                const severityConfig = SEVERITY_CONFIG[incident.severity];
                const statusConfig = STATUS_CONFIG[incident.status];
                const isActive = !['resolved', 'postmortem'].includes(incident.status);

                return (
                  <div
                    key={incident.id}
                    className={`bg-surface/30 border rounded-lg p-4 ${isActive ? severityConfig.border : 'border-border-muted'}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start gap-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${severityConfig.color}`}>
                          {incident.severity.toUpperCase()}
                        </span>
                        <div>
                          <h3 className="text-foreground font-semibold">{incident.title}</h3>
                          {incident.description && (
                            <p className="text-foreground-tertiary text-sm mt-1">{incident.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${statusConfig.color}`}></span>
                        <span className="text-foreground-secondary text-sm">{statusConfig.label}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-tertiary">
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Duration: {formatDuration(incident.detected_at, incident.resolved_at)}</span>
                      </div>
                      {incident.incident_commander && (
                        <div className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span>IC: {incident.incident_commander}</span>
                        </div>
                      )}
                      {incident.affected_services && incident.affected_services.length > 0 && (
                        <div className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                          <span>{incident.affected_services.join(', ')}</span>
                        </div>
                      )}
                      <span className="text-foreground-muted">
                        Detected: {new Date(incident.detected_at).toLocaleString()}
                      </span>
                    </div>

                    {(incident.ticket_url || incident.postmortem_url) && (
                      <div className="flex gap-4 mt-3 pt-3 border-t border-border-muted">
                        {incident.ticket_url && (
                          <a
                            href={incident.ticket_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent text-sm hover:underline"
                          >
                            View Ticket
                          </a>
                        )}
                        {incident.postmortem_url && (
                          <a
                            href={incident.postmortem_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent text-sm hover:underline"
                          >
                            View Postmortem
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background border border-border rounded-xl p-6 w-full max-w-lg mx-4">
              <h2 className="text-xl font-bold text-foreground mb-4">Declare Incident</h2>
              <form onSubmit={handleCreateIncident}>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-foreground-secondary mb-1 block">Title *</label>
                    <input
                      type="text"
                      required
                      value={newIncident.title}
                      onChange={(e) => setNewIncident({ ...newIncident, title: e.target.value })}
                      className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-accent"
                      placeholder="Brief description of the incident"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-foreground-secondary mb-1 block">Description</label>
                    <textarea
                      value={newIncident.description}
                      onChange={(e) => setNewIncident({ ...newIncident, description: e.target.value })}
                      className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-accent h-24"
                      placeholder="Additional details about the incident"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-foreground-secondary mb-1 block">Severity *</label>
                    <select
                      value={newIncident.severity}
                      onChange={(e) => setNewIncident({ ...newIncident, severity: e.target.value as 'sev1' | 'sev2' | 'sev3' | 'sev4' })}
                      className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-accent"
                    >
                      {Object.entries(SEVERITY_CONFIG).map(([key, config]) => (
                        <option key={key} value={key}>{config.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-foreground-secondary mb-1 block">Incident Commander</label>
                    <input
                      type="text"
                      value={newIncident.incident_commander}
                      onChange={(e) => setNewIncident({ ...newIncident, incident_commander: e.target.value })}
                      className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-accent"
                      placeholder="Name of the incident commander"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-foreground-secondary mb-1 block">Affected Services</label>
                    <input
                      type="text"
                      value={newIncident.affected_services}
                      onChange={(e) => setNewIncident({ ...newIncident, affected_services: e.target.value })}
                      className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-accent"
                      placeholder="Comma-separated list of affected services"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-foreground-secondary mb-1 block">Ticket URL</label>
                    <input
                      type="url"
                      value={newIncident.ticket_url}
                      onChange={(e) => setNewIncident({ ...newIncident, ticket_url: e.target.value })}
                      className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-accent"
                      placeholder="https://..."
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-foreground-secondary hover:text-foreground"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600"
                  >
                    Declare Incident
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function IncidentsPage() {
  return (
    <WorkspaceProvider>
      <CommandCenterProvider>
        <CommandCenterHeader />
        <IncidentsContent />
      </CommandCenterProvider>
    </WorkspaceProvider>
  );
}
